import {
  ELEMENT_SELECTION_PATTERN,
  ELEMENT_SELECTION_STRIP_PATTERN,
  isWebSelection,
} from '@/lib/element-selection';
import type { ResponseAnnotation } from '@/lib/response-annotations';
import { parseResponseAnnotatedPrompt } from '@/lib/response-annotations';

/** An element selection recovered from an edited message, before an id is assigned. */
export type EditedElementSelection = { context: string; title: string; kind: 'web' | 'app' };

/** A response annotation recovered from an edited message, before an id is assigned. */
export type EditedResponseAnnotation = ResponseAnnotation & {
  kind: 'chat';
  source: 'Conversation';
};

/**
 * Split a persisted message body back into its editable composer pieces: the
 * plain request text (with embedded element-selection blocks stripped), the
 * element selections carried on it, and its response annotations. Pure so the
 * regex bookkeeping can be unit tested; the shell assigns fresh ids to each
 * recovered selection/annotation because ids are runtime-only.
 */
export function parseEditedMessage(text: string): {
  composerText: string;
  elementSelections: EditedElementSelection[];
  responseAnnotations: EditedResponseAnnotation[];
} {
  const annotatedPrompt = parseResponseAnnotatedPrompt(text);
  const contexts = annotatedPrompt.request.match(ELEMENT_SELECTION_PATTERN) ?? [];
  return {
    composerText: annotatedPrompt.request.replace(ELEMENT_SELECTION_STRIP_PATTERN, '').trim(),
    elementSelections: contexts.map((context) => ({
      context,
      title: context.match(/^Target: (.+)$/m)?.[1]?.replace(/^"|"$/g, '') ?? 'Sélection web',
      kind: isWebSelection(context) ? 'web' : 'app',
    })),
    responseAnnotations: annotatedPrompt.annotations.map((annotation) => ({
      ...annotation,
      kind: 'chat',
      source: 'Conversation',
    })),
  };
}
